'use client';

import Link from 'next/link';
import Icon from '../ui/AppIcon';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface SocialLink {
  name: string;
  icon: string;
  href: string;
}

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const currentYear = new Date().getFullYear();

  const footerSections: FooterSection[] = [
    {
      title: 'Our Approach',
      links: [
        { label: 'Transformation Agenda', href: '/the-transformation-agenda' },
        { label: 'Investment Strategy', href: '/investment-approach' },
        { label: 'Regional Focus', href: '/regional-operations' },
        { label: 'Impact Measurement', href: '/impact-framework' },
      ],
    },
    {
      title: 'Partnership',
      links: [
        { label: 'Partnership Portal', href: '/partnership-portal' },
        { label: 'Investor Relations', href: '/partnership-portal#investors' },
        { label: 'Government Collaboration', href: '/partnership-portal#government' },
        { label: 'Strategic Alliances', href: '/partnership-portal#strategic' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { label: 'Research Reports', href: '/resources/reports' },
        { label: 'Impact Stories', href: '/resources/stories' },
        { label: 'News & Insights', href: '/resources/news' },
        { label: 'Documentation', href: '/resources/documentation' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', href: '/about' },
        { label: 'Leadership Team', href: '/about/leadership' },
        { label: 'Careers', href: '/careers' },
        { label: 'Contact', href: '/contact' },
      ],
    },
  ];

  const socialLinks: SocialLink[] = [
    { name: 'LinkedIn', icon: 'BuildingOfficeIcon', href: 'https://linkedin.com' },
    { name: 'Twitter', icon: 'ChatBubbleLeftRightIcon', href: 'https://twitter.com' },
    { name: 'Email', icon: 'EnvelopeIcon', href: 'mailto:partnerships@grandvilleholdings.com' },
  ];

  const legalLinks: FooterLink[] = [
    { label: 'Privacy Policy', href: '/legal/privacy' },
    { label: 'Terms of Service', href: '/legal/terms' },
    { label: 'Compliance', href: '/legal/compliance' },
    { label: 'Accessibility', href: '/legal/accessibility' },
  ];

  return (
    <footer data-header="dark" className={`bg-brand-charcoal text-gray-300 ${className}`}>
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">
          <div className="lg:col-span-2">
            <Link href="/homepage" className="flex items-center space-x-3">
            <div className="w-24 h-auto">
              <img
                src="/assets/images/logo.png"
                alt="Grandville Holdings Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-headline text-2xl text-yellow-400  font-semibold">
                Grandville Holdings
              </span>
              <span className={`font-body text-sm text-yellow-400 `}>Building What Lasts</span>
            </div>
          </Link>
            <p className="font-body text-sm text-gray-400 leading-relaxed mb-6 max-w-sm">
              Institutional development finance driving sustainable transformation across Africa
              through patient capital, systematic methodology, and authentic partnership.
            </p>
            <div className="flex items-center space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 bg-brand-forest hover:bg-primary rounded-md transition-colors duration-300"
                  aria-label={social.name}
                >
                  <Icon
                    name={social.icon as any}
                    size={20}
                    variant="outline"
                    className="text-white"
                  />
                </a>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:col-span-4">
            {footerSections.map((section) => (
              <div key={section.title}>
                <h3 className="font-headline text-base font-semibold text-white mb-4">
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        className="font-body text-sm text-gray-400 hover:text-accent transition-colors duration-300"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Legal Links Section */}
            <div className="block sm:hidden">
              <h3 className="font-headline text-base font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-3">
                {legalLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="font-body text-sm text-gray-400 hover:text-accent transition-colors duration-300"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="font-body text-sm text-white">
              © {currentYear} Grandville Holdings. All rights reserved.
            </p>
            <div className="hidden sm:flex flex-wrap items-center justify-center gap-6">
              {legalLinks.map((link, index) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="font-body text-sm text-gray-400 hover:text-accent transition-colors duration-300"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
